import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, ChevronRight, Tag, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import AdSlot from "@/components/AdSlot";
import { CATEGORIES, getCategory } from "@/data/blogPosts";
import { useAllPosts } from "@/hooks/useAllPosts";
import { formatDate } from "@/lib/format";

// Helpers that operate on a dynamically-merged posts list (static + AI).
const findBySlug = (posts, slug) => posts.find((p) => p.slug === slug);
const filterByCategory = (posts, cat) => posts.filter((p) => p.category === cat);

// Same curated Unsplash pool used by the backend when Nano Banana is
// unavailable — used as the browser-side fallback when a cover URL 404s
// (e.g. an old post whose ephemeral disk image was wiped by a redeploy).
const FALLBACK_COVERS = [
  "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=1200&h=630&fit=crop",
  "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&h=630&fit=crop",
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=630&fit=crop",
  "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=1200&h=630&fit=crop",
];
const fallbackCoverFor = (slug = "") => {
  let h = 0;
  for (let i = 0; i < slug.length; i += 1) h = ((h << 5) - h + slug.charCodeAt(i)) | 0;
  return FALLBACK_COVERS[Math.abs(h) % FALLBACK_COVERS.length];
};
const onCoverError = (slug) => (e) => {
  if (e.currentTarget.dataset.fallback === "1") return; // avoid infinite loop
  e.currentTarget.dataset.fallback = "1";
  e.currentTarget.src = fallbackCoverFor(slug);
};

function BlogIndex() {
  const posts = useAllPosts();
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
      <SEO
        title="Blog — YouTube Growth, SEO, Monetization & AI Insights"
        description="The Creator Intelligence Tube blog: data-backed articles about YouTube growth, SEO, monetization, analytics and the creator economy."
        path="/blog"
        keywords="youtube growth blog, youtube seo blog, creator economy, youtube monetization, creator tips, youtube analytics"
        jsonLd={{
          "@type": "Blog",
          name: "Creator Intelligence Tube Blog",
          url: "/blog",
          publisher: { "@type": "Organization", name: "Creator Intelligence Tube" },
        }}
      />
      <Breadcrumbs items={[{ name: "Blog", path: "/blog" }]} />
      <div className="overline mt-6">The Blog</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">
        Insights for creators, brands and analysts
      </h1>
      <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
        Data-backed articles about YouTube growth, SEO, monetization, analytics and the creator economy — written by our research team.
      </p>

      {/* Categories */}
      <div className="mt-8 flex flex-wrap gap-2" data-testid="blog-category-nav">
        <Link
          to="/blog"
          className="text-xs font-semibold px-3 py-1.5 rounded-full border border-primary/60 text-primary bg-primary/5"
        >
          All articles
        </Link>
        {CATEGORIES.map((c) => {
          const count = filterByCategory(posts, c.slug).length;
          if (count === 0) return null;
          return (
            <Link
              key={c.slug}
              to={`/blog/category/${c.slug}`}
              className="text-xs font-semibold px-3 py-1.5 rounded-full border border-border/70 hover:border-primary/40 hover:text-primary transition-colors"
              data-testid={`blog-category-${c.slug}`}
            >
              {c.label} <span className="text-muted-foreground font-mono ml-1">{count}</span>
            </Link>
          );
        })}
      </div>

      {/* Featured post (most recent) */}
      {posts[0] && (
        <Link
          to={`/blog/${posts[0].slug}`}
          className="mt-12 card-flat card-hover overflow-hidden block group grid md:grid-cols-[1.2fr_1fr]"
          data-testid="blog-featured-post"
        >
          <div className="aspect-[16/9] md:aspect-auto bg-secondary overflow-hidden">
            <img
              src={posts[0].cover}
              alt={posts[0].title}
              loading="eager"
              onError={onCoverError(posts[0].slug)}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="p-6 sm:p-10 flex flex-col justify-center">
            <div className="overline text-primary">Featured</div>
            <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground font-mono">
              <span>{getCategory(posts[0].category)?.label}</span>
              <span>·</span>
              <span>{formatDate(posts[0].published_at)}</span>
              <span>·</span>
              <span>{posts[0].read_min} min read</span>
            </div>
            <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight group-hover:text-primary transition-colors">
              {posts[0].title}
            </h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
              {posts[0].excerpt}
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
              Read article <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </Link>
      )}

      {/* Ad — between featured post and the article list */}
      <AdSlot slot="blogIndex" format="display" />

      {/* Section heading */}
      <div className="mt-16 flex items-end justify-between">
        <h2 className="text-2xl font-extrabold tracking-tight">All articles</h2>
        <div className="text-xs text-muted-foreground font-mono">{posts.length} articles</div>
      </div>

      {/* Posts grid */}
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-post-list">
        {posts.slice(1).map((p) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            className="card-flat card-hover overflow-hidden block group"
            data-testid={`blog-post-${p.slug}`}
          >
            <div className="aspect-[16/9] bg-secondary overflow-hidden">
              <img src={p.cover} alt={p.title} loading="lazy" onError={onCoverError(p.slug)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground font-mono">
                <span className="text-primary font-semibold uppercase tracking-wider">{getCategory(p.category)?.label}</span>
                <span>·</span>
                <span>{p.read_min} min</span>
              </div>
              <h3 className="mt-2 text-base font-extrabold tracking-tight leading-snug group-hover:text-primary transition-colors line-clamp-2">
                {p.title}
              </h3>
              <p className="mt-2 text-xs text-muted-foreground line-clamp-2 leading-relaxed">{p.excerpt}</p>
              <div className="mt-3 text-[11px] text-muted-foreground font-mono">{formatDate(p.published_at)}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function BlogCategory() {
  const { slug } = useParams();
  const cat = getCategory(slug);
  const allPosts = useAllPosts();
  const posts = filterByCategory(allPosts, slug);
  if (!cat) return <BlogIndex />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
      <SEO
        title={`${cat.label} — Blog`}
        description={`Articles about ${cat.label} from Creator Intelligence Tube.`}
        path={`/blog/category/${cat.slug}`}
      />
      <Breadcrumbs items={[{ name: "Blog", path: "/blog" }, { name: cat.label, path: `/blog/category/${cat.slug}` }]} />
      <div className="overline mt-6">Category</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">{cat.label}</h1>
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {posts.length === 0 && (
          <div className="card-flat p-8 text-muted-foreground">No posts in this category yet. Check back soon.</div>
        )}
        {posts.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} className="card-flat card-hover overflow-hidden block group">
            <div className="aspect-[16/9] bg-secondary overflow-hidden">
              <img src={p.cover} alt={p.title} loading="lazy" onError={onCoverError(p.slug)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-6">
              <div className="text-xs text-muted-foreground font-mono">{formatDate(p.published_at)} · {p.read_min} min read</div>
              <h2 className="mt-2 text-xl font-extrabold tracking-tight group-hover:text-primary transition-colors">{p.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function BlogPost() {
  const { slug } = useParams();
  const allPosts = useAllPosts();
  const post = findBySlug(allPosts, slug);
  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-16">
        <h1 className="text-3xl font-bold">Article not found</h1>
        <Link to="/blog" className="text-primary underline mt-4 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to blog
        </Link>
      </div>
    );
  }
  const cat = getCategory(post.category);
  const related = allPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  const jsonLd = {
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover,
    datePublished: post.published_at,
    dateModified: post.published_at,
    author: { "@type": "Organization", name: post.author },
    publisher: { "@type": "Organization", name: "Creator Intelligence Tube" },
    mainEntityOfPage: `/blog/${post.slug}`,
    articleSection: cat?.label,
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover}
        type="article"
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: "Blog", path: "/blog" },
          { name: cat?.label || "Article", path: `/blog/category/${post.category}` },
          { name: post.title, path: `/blog/${post.slug}` },
        ]}
      />
      <Breadcrumbs items={[
        { name: "Blog", path: "/blog" },
        { name: cat?.label || "Article", path: `/blog/category/${post.category}` },
        { name: post.title, path: `/blog/${post.slug}` },
      ]} />

      <article className="mt-8">
        <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono">
          <Tag className="w-3 h-3" />
          <Link to={`/blog/category/${post.category}`} className="hover:text-primary">{cat?.label}</Link>
          <span>·</span>
          <Calendar className="w-3 h-3" />
          <span>{formatDate(post.published_at)}</span>
          <span>·</span>
          <Clock className="w-3 h-3" />
          <span>{post.read_min} min read</span>
        </div>
        <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1]">{post.title}</h1>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{post.excerpt}</p>

        <div className="mt-8 aspect-[16/9] rounded-2xl overflow-hidden bg-secondary">
          <img src={post.cover} alt={post.title} loading="eager" fetchpriority="high" onError={onCoverError(post.slug)} className="w-full h-full object-cover" />
        </div>

        <div className="mt-10 space-y-8">
          {post.body.map((s, idx) => (
            <section key={s.h}>
              <h2 className="text-2xl font-bold tracking-tight">{s.h}</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">{s.p}</p>
              {s.img && (
                <figure className="mt-5">
                  <div className="aspect-[16/8] rounded-xl overflow-hidden bg-secondary">
                    <img
                      src={s.img.src}
                      alt={s.img.alt}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {s.img.caption && (
                    <figcaption className="mt-2 text-xs text-muted-foreground italic">
                      {s.img.caption}
                    </figcaption>
                  )}
                </figure>
              )}
              {/* Ad — after the first section (top of the article body) */}
              {idx === 0 && <AdSlot slot="articleTop" />}
              {/* Ad — mid-article, roughly halfway through */}
              {idx === Math.floor(post.body.length / 2) && <AdSlot slot="articleMid" />}
            </section>
          ))}
        </div>

        {/* Ad — after all content, before the author line */}
        <AdSlot slot="articleBottom" />

        <div className="mt-12 pt-8 border-t border-border/60">
          <div className="text-xs text-muted-foreground font-mono">Written by {post.author}</div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="text-2xl font-bold tracking-tight">Related articles</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="card-flat card-hover p-5 block group">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{getCategory(r.category)?.label}</div>
                <div className="mt-2 font-bold text-sm group-hover:text-primary transition-colors">{r.title}</div>
                <div className="mt-3 text-xs text-primary font-semibold flex items-center gap-1">
                  Read <ChevronRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export { BlogIndex, BlogCategory, BlogPost };
